/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import { isInspecaoTermografica } from '../../../utils';
import { podeEditarProcedimentoDigitacao } from './permissoes';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { messageError, messageSuccess } from '../../../store/toast/actions';
import BaseService from '../../../services/BaseService';
import PrioridadeProcedimentoChip from '../Nsir/mtbt/components/PrioridadeProcedimentoChip';
import {
  ACESSO_VEICULO_OPCOES,
  ACESSOS_OPCOES,
  EQUIPES_OPCOES,
  INSPECAO_MT_BT_ID,
  PRIORIDADES_NSMP_OPCOES,
  PROCEDIMENTO_NOVO,
  QUERY_PROCEDIMENTO_ID,
  SERVICO_OUTROS_ID,
} from './constants';
import { resumoNomes } from './resumoNomes';
import { ativarProcedimento, inativarProcedimento, listarProcedimentos, mensagemErro } from './api';
import Formulario from './Formulario';

const getFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const raw = params.get(QUERY_PROCEDIMENTO_ID) || params.get('id');
  return raw && String(raw).trim() ? String(raw).trim() : null;
};

const temFiltroAplicado = (filtro) => Object.values(filtro || {}).some((valor) => (
  Array.isArray(valor) ? valor.length > 0 : valor !== undefined && valor !== null && valor !== ''
));

/** Texto completo no tooltip; `lineHeight: inherit` mantém o centro vertical igual ao das células sem renderCell. */
const celulaEscopo = (texto) => (
  <Tooltip title={texto}>
    <Typography
      noWrap
      component="span"
      variant="body2"
      sx={{ display: 'block', lineHeight: 'inherit' }}
    >
      {texto}
    </Typography>
  </Tooltip>
);

/** Tipo de inspeção (MT/BT) e, só quando o procedimento é da termográfica, o chip do subtipo logo abaixo. */
const celulaTipoInspecao = (texto, subtipos) => {
  const termografica = (subtipos || []).find((subtipo) => isInspecaoTermografica(INSPECAO_MT_BT_ID, subtipo?.id));
  if (!termografica) return celulaEscopo(texto);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        noWrap
        component="span"
        variant="body2"
        sx={{ maxWidth: '100%' }}
      >
        {texto}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        label={termografica.nome || 'Termográfica'}
      />
    </Box>
  );
};

/** Tokens de status do tema, mesmos de `renderStatus` (texto `.dark`, borda `.main`), com vermelho para inativo. */
const chipSituacao = (ativo) => (
  <Chip
    size="small"
    variant="outlined"
    label={ativo ? 'Ativo' : 'Inativo'}
    sx={{
      color: ativo ? 'success.dark' : 'error.dark',
      borderColor: ativo ? 'success.main' : 'error.main',
    }}
  />
);

const LINHAS_VISIVEIS = 3;

/** `ACESSO_VEICULO_OPCOES` traz "Indiferente" (vazio), que não faz sentido como filtro. */
const ACESSO_VEICULO_FILTRO = ACESSO_VEICULO_OPCOES
  .filter((opcao) => opcao.id !== '')
  .map((opcao) => ({ value: opcao.id, label: opcao.nome }));

const rotuloOpcao = (opcoes, valor) => opcoes.find((opcao) => String(opcao.value) === String(valor))?.label || String(valor);

/** Região por nível, sem misturar malha, gerência e polo numa lista só. */
const linhasRegiao = (regiao) => [
  { rotulo: 'Malhas', texto: resumoNomes(regiao?.malhas, '') },
  { rotulo: 'Gerências', texto: resumoNomes(regiao?.gerencias, '') },
  { rotulo: 'Polos', texto: resumoNomes(regiao?.polos, '') },
].filter((linha) => linha.texto);

/** Os mesmos campos do bloco "Atributos da inspeção" do cadastro. Categoria não entra: não tem campo no cadastro. */
const linhasAtributos = (row) => {
  const nsmp = row.nsmp || {};
  const veiculo = { true: 'Sim', false: 'Não' }[String(nsmp.acessoVeiculo)];
  return [
    { rotulo: 'Serviços', texto: resumoNomes(row.servico?.tiposServico, '') },
    {
      rotulo: 'Equipe',
      texto: (nsmp.equipes || [])
        .map((item) => rotuloOpcao(EQUIPES_OPCOES, typeof item === 'string' ? item : item?.equipe))
        .join(', '),
    },
    { rotulo: 'Prioridade', texto: (nsmp.prioridades || []).map((item) => rotuloOpcao(PRIORIDADES_NSMP_OPCOES, item)).join(', ') },
    { rotulo: 'Acesso', texto: (nsmp.acessos || []).map((item) => rotuloOpcao(ACESSOS_OPCOES, item)).join(', ') },
    { rotulo: 'Veículo', texto: veiculo || '' },
  ].filter((linha) => linha.texto);
};

/** Linhas "Rótulo: valor". O que passa de LINHAS_VISIVEIS fica só no tooltip, com a contagem no fim da última linha. */
const celulaRotulada = (linhas, vazio) => {
  if (!linhas.length) return celulaEscopo(vazio);
  const visiveis = linhas.slice(0, LINHAS_VISIVEIS);
  const ocultas = linhas.length - visiveis.length;
  const completo = linhas.map((linha) => `${linha.rotulo}: ${linha.texto}`).join('\n');
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{completo}</Box>}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          minWidth: 0,
        }}
      >
        {visiveis.map((linha, indice) => (
          <Typography
            key={linha.rotulo}
            noWrap
            component="span"
            variant="caption"
            sx={{ display: 'block', lineHeight: 1.45 }}
          >
            <Box component="span" sx={{ color: 'text.secondary' }}>{linha.rotulo}: </Box>
            {linha.texto}
            {indice === visiveis.length - 1 && ocultas > 0 ? ` +${ocultas}` : ''}
          </Typography>
        ))}
      </Box>
    </Tooltip>
  );
};

/** Título com a data da última alteração logo abaixo. */
const celulaTitulo = (row) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      minWidth: 0,
    }}
  >
    <Tooltip title={row.titulo}>
      <Typography noWrap component="span" variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
        {row.titulo}
      </Typography>
    </Tooltip>
    {row.atualizadoEm && (
      <Typography
        noWrap
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.4, fontVariantNumeric: 'tabular-nums' }}
      >
        Atualizado em {moment(row.atualizadoEm).format('DD/MM/YYYY HH:mm')}
      </Typography>
    )}
  </Box>
);

const COLUNAS = [
  {
    field: 'titulo',
    headerName: 'Título',
    flex: 1.2,
    minWidth: 220,
    renderCell: (params) => celulaTitulo(params.row),
  },
  {
    field: 'tipoInspecao',
    headerName: 'Tipo de inspeção',
    flex: 0.7,
    minWidth: 150,
    valueGetter: (_value, row) => resumoNomes(row.inspecao?.tiposInspecao, 'Inspeção MT/BT'),
    renderCell: (params) => celulaTipoInspecao(params.value, params.row.inspecao?.subtipos),
  },
  {
    field: 'regiao',
    headerName: 'Região',
    flex: 1,
    minWidth: 170,
    sortable: false,
    renderCell: (params) => celulaRotulada(linhasRegiao(params.row.regiao), 'Todas as regiões'),
  },
  {
    field: 'atributos',
    headerName: 'Atributos da inspeção',
    flex: 1.3,
    minWidth: 230,
    sortable: false,
    renderCell: (params) => celulaRotulada(linhasAtributos(params.row), 'Qualquer NSMP'),
  },
  {
    field: 'prioridade',
    headerName: 'Importância',
    width: 120,
    renderCell: (params) => <PrioridadeProcedimentoChip prioridade={params.row.prioridade} />,
  },
  {
    field: 'ativo',
    headerName: 'Situação',
    width: 110,
    renderCell: (params) => chipSituacao(params.row.ativo),
  },
];

const ProcedimentoDigitacao = ({
  authData,
  moduleConfigurations,
  history,
  location,
  messageSuccess: toastOk,
  messageError: toastErro,
}) => {
  // Super admin ou módulo: edita. Os demais só consultam (olho + formulário somente leitura).
  const podeEditar = podeEditarProcedimentoDigitacao(authData, moduleConfigurations);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [localFilter, setLocalFilter] = useState({});
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [inativarId, setInativarId] = useState(null);
  const [ativarId, setAtivarId] = useState(null);
  const [servicoOptions, setServicoOptions] = useState([]);
  const [subtipoOptions, setSubtipoOptions] = useState([]);

  const setInUrl = useCallback((value) => {
    const params = new URLSearchParams(location.search);
    params.delete('id');
    if (!value) params.delete(QUERY_PROCEDIMENTO_ID);
    else params.set(QUERY_PROCEDIMENTO_ID, String(value));
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const raw = getFromSearch(location.search);
    // Sem permissão, link de criação (?procedimentoId=novo) não abre o formulário.
    if (!raw || (raw === PROCEDIMENTO_NOVO && !podeEditar)) {
      setOpen(false);
      setId(null);
      return;
    }
    setId(raw === PROCEDIMENTO_NOVO ? null : raw);
    setOpen(true);
  }, [location.search, podeEditar]);

  useEffect(() => {
    let cancelado = false;
    const carregarOpcoes = async () => {
      try {
        const [servicoRes, subtipoRes] = await Promise.all([
          BaseService.get({ url: 'options/NSMP_SERVICOS_TIPO_SERVICO' }),
          BaseService.get({ url: 'options/NSIR_SUBTIPOS' }),
        ]);
        if (cancelado) return;
        setServicoOptions(
          (servicoRes.data || [])
            .filter((opcao) => opcao.id !== SERVICO_OUTROS_ID)
            .map((opcao) => ({ value: opcao.id, label: opcao.value })),
        );
        setSubtipoOptions(
          (subtipoRes.data || [])
            .filter((opcao) => String(opcao.parentId) === String(INSPECAO_MT_BT_ID))
            .map((opcao) => ({ value: opcao.id, label: opcao.value })),
        );
      } catch (error) {
        console.error(error);
      }
    };
    carregarOpcoes();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setLoading(true);
      setErro(false);
      try {
        const data = await listarProcedimentos({ page, pageSize, filter: localFilter });
        if (!cancelado) {
          setRows(data.rows);
          setTotalRows(data.totalRows);
        }
      } catch (error) {
        if (!cancelado) {
          setErro(true);
          toastErro({ message: mensagemErro(error, 'Erro ao listar os procedimentos.') });
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, [page, pageSize, localFilter, reload]);

  const onClose = (salvou) => {
    setInUrl(null);
    if (salvou) setReload((atual) => !atual);
  };

  const confirmarInativar = async () => {
    const alvo = inativarId;
    setInativarId(null);
    try {
      await inativarProcedimento(alvo);
      toastOk({ message: 'Procedimento inativado.' });
      setReload((atual) => !atual);
    } catch (error) {
      toastErro({ message: mensagemErro(error, 'Erro ao inativar o procedimento.') });
    }
  };

  const confirmarAtivar = async () => {
    const alvo = ativarId;
    setAtivarId(null);
    try {
      await ativarProcedimento(alvo);
      toastOk({ message: 'Procedimento ativado.' });
      setReload((atual) => !atual);
    } catch (error) {
      toastErro({ message: mensagemErro(error, 'Erro ao ativar o procedimento.') });
    }
  };

  let mensagemVazia = podeEditar
    ? 'Nenhum procedimento cadastrado. Use “Novo” para criar o primeiro.'
    : 'Nenhum procedimento cadastrado.';
  if (erro) mensagemVazia = 'Não foi possível carregar os procedimentos.';
  else if (temFiltroAplicado(localFilter)) mensagemVazia = 'Nenhum procedimento para os filtros aplicados.';

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Stack spacing={0.5}>
            <Typography variant="h4">Procedimentos de Digitação</Typography>
            <Typography variant="body2" color="text.secondary">
              Textos que o digitador vê na NSIR MT/BT, por região e serviço.
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>
        {erro && (
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => setReload((atual) => !atual)}
              >
                Tentar novamente
              </Button>
            )}
          >
            Não foi possível carregar os procedimentos.
          </Alert>
        )}
        <TableV2
          rows={rows}
          totalRows={totalRows}
          loading={loading}
          rowHeight={68}
          emptyMessage={mensagemVazia}
          autoPageSize={false}
          pageSize={pageSize}
          filterData={localFilter}
          onPageModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          headerActions={podeEditar ? [{
            label: 'Novo',
            onClick: () => setInUrl(PROCEDIMENTO_NOVO),
            startIcon: <AddIcon />,
            variant: 'contained',
          }] : []}
          onEdit={podeEditar ? (rowId) => setInUrl(rowId) : undefined}
          onView={podeEditar ? undefined : (rowId) => setInUrl(rowId)}
          actions={podeEditar ? [{
            key: 'inativar',
            title: 'Inativar',
            icon: <BlockOutlinedIcon fontSize="small" />,
            show: (row) => row.ativo,
            onClick: (rowId) => setInativarId(rowId),
          }, {
            key: 'ativar',
            title: 'Ativar',
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            show: (row) => !row.ativo,
            onClick: (rowId) => setAtivarId(rowId),
          }] : []}
          columns={COLUNAS}
        />
        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          fields={[
            { field: 'useNome', nomeLabel: 'Texto', md: 12, xs: 12 },
            { field: 'useMalha', md: 4, xs: 12 },
            { field: 'useGerencia', md: 4, xs: 12 },
            { field: 'usePolo', md: 4, xs: 12 },
            { field: 'useSearchSelect', key: 'subtipoId', label: 'Subtipo', options: subtipoOptions, md: 6, xs: 12 },
            { field: 'useSearchSelect', key: 'tipoServicoId', label: 'Serviço', options: servicoOptions, md: 6, xs: 12 },
            { field: 'useSearchSelect', key: 'equipe', label: 'Equipe indicada', options: EQUIPES_OPCOES, md: 6, xs: 12 },
            { field: 'useSearchSelect', key: 'prioridadeNsmp', label: 'Prioridade da NSMP', options: PRIORIDADES_NSMP_OPCOES, md: 6, xs: 12 },
            { field: 'useSearchSelect', key: 'acesso', label: 'Acesso', options: ACESSOS_OPCOES, md: 6, xs: 12 },
            { field: 'useSearchSelect', key: 'acessoVeiculo', label: 'Acesso de veículo', options: ACESSO_VEICULO_FILTRO, md: 6, xs: 12 },
            { field: 'useSimNao', key: 'ativo', label: 'Ativo', md: 6, xs: 12 },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setPage(0);
            setOpenFilter(false);
          }}
        />
        <Formulario
          id={id}
          open={open}
          somenteLeitura={!podeEditar}
          onClose={onClose}
        />
        <Dialog open={!!ativarId} onClose={() => setAtivarId(null)}>
          <DialogTitle>Ativar procedimento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              O procedimento volta a aparecer para o digitador, com o mesmo conteúdo e as mesmas regras.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAtivarId(null)}>Cancelar</Button>
            <Button onClick={confirmarAtivar}>Ativar</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={!!inativarId} onClose={() => setInativarId(null)}>
          <DialogTitle>Inativar procedimento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              O procedimento deixa de aparecer para o digitador. O cadastro permanece para consulta de quem administra.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInativarId(null)}>Cancelar</Button>
            <Button color="error" onClick={confirmarInativar}>Inativar</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

ProcedimentoDigitacao.propTypes = {
  authData: PropTypes.object,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  messageSuccess: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

ProcedimentoDigitacao.defaultProps = {
  authData: null,
  moduleConfigurations: [],
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  messageSuccess,
  messageError,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProcedimentoDigitacao));
